import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import GenericModal from "./GenericModal";
import ModalAdminAddError from "./ModalAdminAddError";
import ModalAdminDiscard from "./ModalAdminDiscard";
import ModalUpdateSuccess from "./Modalupdatesuccess";
import Navbar from "./Navbar";
import { InfoCircleIcon } from "./assets";
import BadgeWrapper from "./prtnr/ui/BadgeType";
import RichTextEditor from "./prtnr/ui/RichTextEditor";
import { fetchVendorItems } from "./redux/slices/VendorThunks";
import {
  addVendorsonhome,
  sendVendorsEmail,
  updateVendorsonhome,
  uploadImage,
} from "./redux/slices/VendorsonhomeThunks";

export default function AddOrEditVendor() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const vendorFromNav = location.state?.vendor;
  const sector = location.state?.sector;
  const isEditing = !!vendorFromNav;

  // modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingTrustedBy, setIsUploadingTrustedBy] = useState(false);
  const [generalError, setgeneralError] = useState("");

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const openSuccessModal = () => setIsSuccessModalOpen(true);
  const closeSuccessModal = () => setIsSuccessModalOpen(false);
  const openErrorModal = () => setIsErrorModalOpen(true);
  const closeErrorModal = () => setIsErrorModalOpen(false);

  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // unified state for add/edit
  const [formData, setFormData] = useState(
    vendorFromNav || {
      company_name: "",
      compay_logo: "",
      sector: "",
      company_size: "",
      locations: "",
      vendor_hq: "",
      typical_budget: "",
      typical_decision_maker: "",
      typical_scenario: "",
      typical_pain_points: "",
      year_founded: "",
      average_sales_cycle_in_months: "",
      company_website_url: "",
      website_thumbnail: "",
      no_of_employees: "",
      vendor_firstname: "",
      vendor_lastname: "",
      vendor_work_email: "",
      vendor_profile_status: "Draft",
      ideal_profile: "",
      short_description: "",
      vendor_industry_sector: [],
      message: "",
      ceo_name: "",
      picture: "",
      about: "",
      contact_number: "",
      reward: "",
      video_url: "",
      trusted_by: [],
      created_at: Date.now(),
    }
  );

  // Enhanced validation configuration
  const validationConfig = {
    vendor_firstname: {
      required: true,
      minLength: 2,
      maxLength: 50,
      pattern: /^[a-zA-Z\s'-]+$/,
      errorMessages: {
        required: "First name is required",
        minLength: "First name must be at least 2 characters",
        maxLength: "First name cannot exceed 50 characters",
        pattern:
          "First name can only contain letters, spaces, hyphens, and apostrophes",
      },
    },
    vendor_lastname: {
      required: true,
      minLength: 2,
      maxLength: 50,
      pattern: /^[a-zA-Z\s'-]+$/,
      errorMessages: {
        required: "Last name is required",
        minLength: "Last name must be at least 2 characters",
        maxLength: "Last name cannot exceed 50 characters",
        pattern:
          "Last name can only contain letters, spaces, hyphens, and apostrophes",
      },
    },
    vendor_work_email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      errorMessages: {
        required: "Work email is required",
        pattern: "Please enter a valid email address",
      },
    },
    contact_number: {
      required: true,
      custom: (value) => isValidPhone(value),
      errorMessages: {
        required: "Contact number is required",
        custom: "Please enter a valid phone number",
      },
    },
    company_name: {
      required: true,
      minLength: 2,
      maxLength: 100,
      errorMessages: {
        required: "Company name is required",
        minLength: "Company name must be at least 2 characters",
        maxLength: "Company name cannot exceed 100 characters",
      },
    },
    compay_logo: {
      required: true,
      errorMessages: {
        required: "Company logo is required",
      },
    },
    company_website_url: {
      required: true,
      pattern: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
      errorMessages: {
        required: "Company website is required",
        pattern: "Please enter a valid website URL",
      },
    },

    website_thumbnail: {
      required: true,
      errorMessages: {
        required: "Website thumbnail is required",
      },
    },
    year_founded: {
      required: true,
      pattern: /^\d{4}$/,
      custom: (value) => {
        const year = parseInt(value);
        const currentYear = new Date().getFullYear();
        return year >= 1800 && year <= currentYear;
      },
      errorMessages: {
        required: "Year founded is required",
        pattern: "Please enter a valid 4-digit year",
        custom: "Year must be between 1800 and current year",
      },
    },
    vendor_hq: {
      required: true,
      minLength: 2,
      maxLength: 100,
      errorMessages: {
        required: "Vendor HQ is required",
        minLength: "Vendor HQ must be at least 2 characters",
        maxLength: "Vendor HQ cannot exceed 100 characters",
      },
    },
    sector: {
      required: true,
      errorMessages: {
        required: "Sector is required",
      },
    },
    company_size: {
      required: true,
      errorMessages: {
        required: "Company size is required",
      },
    },
    locations: {
      required: true,
      errorMessages: {
        required: "Locations are required",
      },
    },
    typical_budget: {
      required: true,
      errorMessages: {
        required: "Typical budget is required",
      },
    },
    typical_decision_maker: {
      required: true,
      errorMessages: {
        required: "Typical decision maker is required",
      },
    },
    typical_scenario: {
      required: true,
      errorMessages: {
        required: "Typical scenario is required",
      },
    },
    typical_pain_points: {
      required: true,
      errorMessages: {
        required: "Typical pain points are required",
      },
    },
    average_sales_cycle_in_months: {
      required: true,
      errorMessages: {
        custom: "Sales cycle must be between 1 and 120 months",
      },
    },
    vendor_industry_sector: {
      required: true,
      custom: (value) => Array.isArray(value) && value.length > 0,
      errorMessages: {
        required: "At least one industry sector is required",
        custom: "At least one industry sector is required",
      },
    },
    short_description: {
      required: true,
      maxLength: 200,
      errorMessages: {
        required: "Short description is required",
        maxLength: "Description cannot exceed 200 characters",
      },
    },
    ideal_profile: {
      required: true,
      errorMessages: {
        required: "Ideal profile summary is required",
      },
    },
    no_of_employees: {
      required: true,
      errorMessages: {
        required: "Number of employees is required",
      },
    },
    reward: {
      required: true,
      pattern: /^\d+$/,
      custom: (value) => parseInt(value) > 0,
      errorMessages: {
        required: "Reward amount is required",
        pattern: "Please enter a valid number",
        custom: "Reward amount must be greater than 0",
      },
    },
  };

  // refs for validation
  const fieldRefs = {
    reward: useRef(null),
    no_of_employees: useRef(null),
    year_founded: useRef(null),
    vendor_firstname: useRef(null),
    vendor_lastname: useRef(null),
    vendor_work_email: useRef(null),
    company_name: useRef(null),
    compay_logo: useRef(null),
    company_website_url: useRef(null),
    website_thumbnail: useRef(null),

    vendor_hq: useRef(null),
    sector: useRef(null),
    company_size: useRef(null),
    locations: useRef(null),
    typical_budget: useRef(null),
    typical_decision_maker: useRef(null),
    typical_scenario: useRef(null),
    typical_pain_points: useRef(null),
    average_sales_cycle_in_months: useRef(null),
    vendor_industry_sector: useRef(null),
    contact_number: useRef(null),
    short_description: useRef(null),
    ideal_profile: useRef(null),
    video_url: useRef(null),
  };

  // Enhanced validation function
  const validateField = (fieldName, value) => {
    const config = validationConfig[fieldName];
    if (!config) return null;

    const trimmedValue = typeof value === "string" ? value.trim() : value;

    // Required validation
    if (config.required) {
      // Handle array values (like multi-select)
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return config.errorMessages.required;
        }
      } else if (!trimmedValue || trimmedValue === "") {
        return config.errorMessages.required;
      }
    }

    // Skip other validations if field is empty and not required
    if (!trimmedValue && !config.required) return null;

    // Length validations
    if (config.minLength && trimmedValue.length < config.minLength) {
      return config.errorMessages.minLength;
    }

    if (config.maxLength && trimmedValue.length > config.maxLength) {
      return config.errorMessages.maxLength;
    }

    // Pattern validation
    if (config.pattern && !config.pattern.test(trimmedValue)) {
      return config.errorMessages.pattern;
    }

    // Custom validation
    if (config.custom && !config.custom(trimmedValue)) {
      return config.errorMessages.custom;
    }

    return null;
  };

  // Validate all fields
  const validateAllFields = () => {
    const errors = {};
    let hasErrors = false;

    Object.keys(validationConfig).forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
        hasErrors = true;
      }
    });

    setFieldErrors(errors);
    return !hasErrors;
  };

  // Real-time field validation
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

    // Clear error when user starts typing
    if (fieldErrors[fieldName]) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
    }

    // Validate on blur for better UX
    if (hasSubmitted) {
      const error = validateField(fieldName, value);
      setFieldErrors((prev) => ({ ...prev, [fieldName]: error }));
    }
  };

  // file/image handler
  const handleFileChange = async (key, file) => {
    if (!file) {
      handleFieldChange(key, "");
      return;
    }

    try {
      const res = await dispatch(uploadImage(file));
      handleFieldChange(key, res.payload.fileURL);
    } catch (e) {
      console.error("File upload error:", e);
      setFieldErrors((prev) => ({
        ...prev,
        [key]: "Failed to upload file. Please try again.",
      }));
    }
  };

  // Multiple file handler for trusted_by
  const handleMultipleFileChange = async (files) => {
    if (!files || files.length === 0) return;

    const currentImages = formData.trusted_by || [];

    // Check if adding new files would exceed the limit of 5
    if (currentImages.length + files.length > 5) {
      setFieldErrors((prev) => ({
        ...prev,
        trusted_by: "Maximum 5 images allowed",
      }));
      return;
    }

    setIsUploadingTrustedBy(true);
    try {
      const uploadPromises = Array.from(files).map((file) =>
        dispatch(uploadImage(file))
      );
      const results = await Promise.all(uploadPromises);
      const newImageUrls = results.map((res) => res.payload.fileURL);

      handleFieldChange("trusted_by", [...currentImages, ...newImageUrls]);
      // Clear any previous errors
      setFieldErrors((prev) => ({
        ...prev,
        trusted_by: null,
      }));
    } catch (e) {
      console.error("File upload error:", e);
      setFieldErrors((prev) => ({
        ...prev,
        trusted_by: "Failed to upload files. Please try again.",
      }));
    } finally {
      setIsUploadingTrustedBy(false);
    }
  };

  // Remove image from trusted_by
  const removeTrustedByImage = (index) => {
    const updatedImages = [...formData.trusted_by];
    updatedImages.splice(index, 1);
    handleFieldChange("trusted_by", updatedImages);
  };

  // Enhanced UK phone validation
  function isValidPhone(phone) {
    if (!phone) return false;

    // Remove spaces, dashes, parentheses
    const cleaned = phone.replace(/[\s()-]/g, "");

    // General international pattern:
    // Optional '+' then 7–15 digits
    return /^\+?\d{7,15}$/.test(cleaned);
  }

  // Scroll to first error
  const scrollToFirstError = () => {
    const errorFields = Object.keys(fieldErrors).filter(
      (field) => fieldErrors[field]
    );

    if (errorFields.length === 0) return;

    // Find the field with the smallest Y position (topmost on the page)
    let topMostField = null;
    let minY = Infinity;

    errorFields.forEach((field) => {
      const ref = fieldRefs[field]?.current;

      // Check if ref exists and has getBoundingClientRect method (is a DOM element)
      if (ref && typeof ref.getBoundingClientRect === "function") {
        const rect = ref.getBoundingClientRect();
        const absoluteY = rect.top + window.scrollY;

        if (absoluteY < minY) {
          minY = absoluteY;
          topMostField = field;
        }
      }
    });

    // Scroll to and focus the topmost error field
    if (topMostField && fieldRefs[topMostField]?.current) {
      const element = fieldRefs[topMostField].current;
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Only focus if the element supports it
      if (typeof element.focus === "function") {
        element.focus();
      }
    }
  };

  // Enhanced save handler
  const handleSave = async () => {
    setHasSubmitted(true);
    setIsSubmitting(true);

    try {
      // Validate all fields
      const isValid = validateAllFields();

      if (!isValid) {
        scrollToFirstError();
        return;
      }

      let res;
      if (isEditing) {
        res = await dispatch(updateVendorsonhome(formData));
      } else {
        res = await dispatch(addVendorsonhome(formData));
      }
      // fetch vendors again after save
      await dispatch(fetchVendorItems());

      if (res?.payload?.message === "Email already exists!") {
        openErrorModal();
        setFieldErrors({
          vendor_work_email: "This email address is already registered",
        });
        if (fieldRefs.vendor_work_email?.current) {
          fieldRefs.vendor_work_email.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          fieldRefs.vendor_work_email.current.focus();
        }
        return;
      }

      if (
        res?.payload?.account_id ||
        res?.payload?._id ||
        res?.payload?.success
      ) {
        setFormData((prev) => ({
          ...prev,
          account_id: res.payload.account_id,
        }));
        openSuccessModal();
      } else {
        setgeneralError("Something went wrong");
      }
    } catch (error) {
      console.error("Save error:", error);
      setgeneralError("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSend = async () => {
    if (!formData?._id) {
      setFieldErrors((prev) => ({
        ...prev,
        vendor_work_email:
          "Please save the vendor first before sending confirmation email",
      }));
      return;
    }

    const resdata = { id: formData?._id };

    try {
      // unwrap gives you the actual payload directly
      const res = await dispatch(sendVendorsEmail(resdata)).unwrap();

      if (res && res._id) {
        openSuccessModal();
      }
    } catch (error) {
      console.error("Email send failed:", error);
    }
  };

  const isRichTextEmpty = (value) => {
    if (!value) return true; // handles null/undefined/empty string
    if (typeof value !== "string") return false; // skip non-strings

    const stripped = value.replace(/<[^>]*>/g, "").trim();
    return stripped.length === 0;
  };
  const getFieldError = (fieldName) => {
    const value = formData[fieldName];

    // Skip non-string fields that can't be empty
    if (typeof value !== "string") return fieldErrors[fieldName] || null;

    return (
      hasSubmitted &&
      (fieldErrors[fieldName] ||
        (validationConfig[fieldName]?.required && isRichTextEmpty(value)))
    );
  };

  const getFieldErrorMessage = (fieldName) => {
    const value = formData[fieldName];

    if (fieldErrors[fieldName]) return fieldErrors[fieldName];

    if (
      hasSubmitted &&
      validationConfig[fieldName]?.required &&
      typeof value === "string" &&
      isRichTextEmpty(value)
    ) {
      return validationConfig[fieldName].errorMessages.required;
    }

    return "";
  };

  return (
    <RootWrapperAdminvendorsthree>
      <Navbar />
      <Dashboard>
        <Wrapper>
          <Right>
            <DashboardHeader>
              <Frame1462>
                <LinkButton>
                  <BackContainer
                    onClick={() => {
                      isEditing
                        ? navigate(`/adminvendorsone/${vendorFromNav._id}`, {
                            state: { sector: sector },
                          })
                        : navigate(-1);
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3334 8H2.66675"
                        stroke="#3B5A6F"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M6.66675 12L2.66675 8L6.66675 4"
                        stroke="#6162C5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <BackText>Back</BackText>
                  </BackContainer>
                </LinkButton>
                <WelcomeJames>
                  {isEditing ? "Edit Vendor" : "Add New Vendor"}{" "}
                </WelcomeJames>
              </Frame1462>
              <Widthdraw />
            </DashboardHeader>
            <Frame1509>
              <Frame1389
                style={{
                  backgroundColor: formData?.website_thumbnail
                    ? "transparent"
                    : "#e0e4f4",
                }}
              >
                <Image20 src={formData?.website_thumbnail} />
              </Frame1389>
              <Logo_0001
                style={{
                  backgroundColor: formData?.compay_logo
                    ? "transparent"
                    : "#e0e4f4",
                }}
              >
                <BadgeWrapper badgeType={formData?.vendor_badge} />

                <ImageLogo src={formData?.compay_logo} />
              </Logo_0001>
              <Frame1406>
                <GeneralInformation>Primary Contact </GeneralInformation>

                <Frame1409>
                  <PRTNR.Input
                    ref={fieldRefs.vendor_firstname}
                    label="First Name"
                    value={formData.vendor_firstname}
                    onChange={(e) =>
                      handleFieldChange("vendor_firstname", e.target.value)
                    }
                    required
                    error={getFieldError("vendor_firstname")}
                    errorMessage={getFieldErrorMessage("vendor_firstname")}
                  />

                  <PRTNR.Input
                    ref={fieldRefs.vendor_lastname}
                    label="Last Name"
                    value={formData.vendor_lastname}
                    onChange={(e) =>
                      handleFieldChange("vendor_lastname", e.target.value)
                    }
                    required
                    error={getFieldError("vendor_lastname")}
                    errorMessage={getFieldErrorMessage("vendor_lastname")}
                  />
                </Frame1409>

                <PRTNR.Input
                  ref={fieldRefs.contact_number}
                  label={
                    <LabelWrapper>
                      <span>
                        Phone <Required>*</Required>
                      </span>
                      <TooltipWrapper>
                        <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                        <TooltipText className="tooltip">
                          For Admin purposes only, not visible on public
                          profile. Please ensure it is your direct work +M or
                          +T.
                        </TooltipText>
                      </TooltipWrapper>
                    </LabelWrapper>
                  }
                  value={formData?.contact_number}
                  onChange={(e) =>
                    handleFieldChange("contact_number", e.target.value)
                  }
                  error={getFieldError("contact_number")}
                  errorMessage={getFieldErrorMessage("contact_number")}
                />

                <PRTNR.Input
                  ref={fieldRefs.vendor_work_email}
                  label="Work email address"
                  value={formData.vendor_work_email}
                  onChange={(e) =>
                    handleFieldChange("vendor_work_email", e.target.value)
                  }
                  button={
                    isEditing && (
                      <SendButton type="button" onClick={handleEmailSend}>
                        Send Confirmation
                      </SendButton>
                    )
                  }
                  required
                  error={getFieldError("vendor_work_email")}
                  errorMessage={getFieldErrorMessage("vendor_work_email")}
                />
              </Frame1406>

              {/* Vendor Information Section */}
              <Frame1406>
                <VendorInformation>Vendor Information</VendorInformation>
                <Frame1407>
                  <PRTNR.Input
                    ref={fieldRefs.company_name}
                    label="Company name"
                    value={formData?.company_name}
                    onChange={(e) =>
                      handleFieldChange("company_name", e.target.value)
                    }
                    required
                    error={getFieldError("company_name")}
                    errorMessage={getFieldErrorMessage("company_name")}
                  />

                  <PRTNR.Input
                    ref={fieldRefs.short_description}
                    label={
                      <LabelWrapper>
                        <span>
                          Short Description <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            Less than 200 characters.
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData?.short_description}
                    onChange={(e) =>
                      handleFieldChange("short_description", e.target.value)
                    }
                    error={getFieldError("short_description")}
                    errorMessage={getFieldErrorMessage("short_description")}
                  />

                  <PRTNR.MultiSelect
                    ref={fieldRefs.vendor_industry_sector}
                    label="Industry sector"
                    value={formData?.vendor_industry_sector}
                    onChange={(e) =>
                      handleFieldChange(
                        "vendor_industry_sector",
                        e.target.value
                      )
                    }
                    options={sector}
                    required
                    error={getFieldError("vendor_industry_sector")}
                    errorMessage={getFieldErrorMessage(
                      "vendor_industry_sector"
                    )}
                  />

                  <PRTNR.FileInput
                    ref={fieldRefs.compay_logo}
                    label="Company Logo"
                    helpText="JPG, GIF or PNG. Max file size 800kb"
                    accept=".jpg,.jpeg,.gif,.png"
                    onChange={(file) => handleFileChange("compay_logo", file)}
                    name="company_logo"
                    value={formData.compay_logo}
                    required
                    error={getFieldError("compay_logo")}
                    errorMessage={getFieldErrorMessage("compay_logo")}
                  />

                  <PRTNR.Input
                    ref={fieldRefs.company_website_url}
                    label="Company Website"
                    value={formData.company_website_url}
                    onChange={(e) =>
                      handleFieldChange("company_website_url", e.target.value)
                    }
                    required
                    error={getFieldError("company_website_url")}
                    errorMessage={getFieldErrorMessage("company_website_url")}
                  />

                  <PRTNR.FileInput
                    ref={fieldRefs.website_thumbnail}
                    label="Website Thumbnail"
                    helpText="JPG, GIF or PNG. Max file size 800kb"
                    accept=".jpg,.jpeg,.gif,.png"
                    onChange={(file) =>
                      handleFileChange("website_thumbnail", file)
                    }
                    name="website_thumbnail"
                    value={formData.website_thumbnail}
                    required
                    error={getFieldError("website_thumbnail")}
                    errorMessage={getFieldErrorMessage("website_thumbnail")}
                  />
                </Frame1407>
              </Frame1406>

              <Frame1406>
                <IdealClientProfile>Ideal Client Profile</IdealClientProfile>
                <Frame1407>
                  <RichTextEditor
                    ref={fieldRefs.ideal_profile}
                    label="Summary"
                    value={formData?.ideal_profile || ""}
                    onChange={(content) => {
                      setFormData((prev) =>
                        prev.ideal_profile !== content
                          ? { ...prev, ideal_profile: content }
                          : prev
                      );
                    }}
                    minHeight="100px"
                    error={getFieldError("ideal_profile")}
                    errorMessage={getFieldErrorMessage("ideal_profile")}
                  />

                  <PRTNR.Input
                    ref={fieldRefs.sector}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Sector <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            In which sectors do your ideal client reside in?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData.sector}
                    onChange={(e) =>
                      handleFieldChange("sector", e.target.value)
                    }
                    error={getFieldError("sector")}
                    errorMessage={getFieldErrorMessage("sector")}
                  />

                  <RichTextEditor
                    ref={fieldRefs.company_size}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Company Size <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            What is the size of your ideal client? (number of
                            employees, and/or revenue)
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData?.company_size || ""}
                    onChange={(content) => {
                      setFormData((prev) =>
                        prev.company_size !== content
                          ? { ...prev, company_size: content }
                          : prev
                      );
                    }}
                    minHeight="100px"
                    error={getFieldError("company_size")}
                    errorMessage={getFieldErrorMessage("company_size")}
                  />
                  <PRTNR.Input
                    ref={fieldRefs.locations}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Location <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            Where are your ideal clients located (at a country
                            level)?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData.locations}
                    onChange={(e) =>
                      handleFieldChange("locations", e.target.value)
                    }
                    error={getFieldError("locations")}
                    errorMessage={getFieldErrorMessage("locations")}
                  />
                  <RichTextEditor
                    ref={fieldRefs.typical_budget}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Client Budget <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            What typical budget does your ideal client have?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData?.typical_budget || ""}
                    onChange={(content) => {
                      setFormData((prev) =>
                        prev.typical_budget !== content
                          ? { ...prev, typical_budget: content }
                          : prev
                      );
                    }}
                    minHeight="100px"
                    error={getFieldError("typical_budget")}
                    errorMessage={getFieldErrorMessage("typical_budget")}
                  />
                  <PRTNR.Input
                    ref={fieldRefs.typical_decision_maker}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Decision Maker <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            What job titles are the typical decision maker for
                            your services?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData.typical_decision_maker}
                    onChange={(e) =>
                      handleFieldChange(
                        "typical_decision_maker",
                        e.target.value
                      )
                    }
                    error={getFieldError("typical_decision_maker")}
                    errorMessage={getFieldErrorMessage(
                      "typical_decision_maker"
                    )}
                  />
                  <RichTextEditor
                    ref={fieldRefs.typical_scenario}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Scenario <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            In what typical scenarios do your ideal clients come
                            to you?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData?.typical_scenario || ""}
                    onChange={(content) => {
                      setFormData((prev) =>
                        prev.typical_scenario !== content
                          ? { ...prev, typical_scenario: content }
                          : prev
                      );
                    }}
                    minHeight="100px"
                    error={getFieldError("typical_scenario")}
                    errorMessage={getFieldErrorMessage("typical_scenario")}
                  />

                  <RichTextEditor
                    ref={fieldRefs.typical_pain_points}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Pain Points <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            What pain points do your clients typically have?
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData?.typical_pain_points || ""}
                    onChange={(content) => {
                      setFormData((prev) =>
                        prev.typical_pain_points !== content
                          ? { ...prev, typical_pain_points: content }
                          : prev
                      );
                    }}
                    minHeight="100px"
                    error={getFieldError("typical_pain_points")}
                    errorMessage={getFieldErrorMessage("typical_pain_points")}
                  />

                  <PRTNR.Input
                    ref={fieldRefs.average_sales_cycle_in_months}
                    label={
                      <LabelWrapper>
                        <span>
                          Typical Deal Cycle <Required>*</Required>
                        </span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            How long is the typical deal cycle? (i.e how long
                            from first contact to contract signing)
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    value={formData.average_sales_cycle_in_months}
                    onChange={(e) =>
                      handleFieldChange(
                        "average_sales_cycle_in_months",
                        e.target.value
                      )
                    }
                    error={getFieldError("average_sales_cycle_in_months")}
                    errorMessage={getFieldErrorMessage(
                      "average_sales_cycle_in_months"
                    )}
                  />
                </Frame1407>
              </Frame1406>

              <Frame1501>
                <About>About</About>

                <PrtnrTextarea>
                  <GFieldsStacked_0019>
                    <RichTextEditor
                      label={
                        <LabelWrapper>
                          <span>About</span>
                          <TooltipWrapper>
                            <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                            <TooltipText className="tooltip">
                              Describe your company's services.
                            </TooltipText>
                          </TooltipWrapper>
                        </LabelWrapper>
                      }
                      value={formData?.about || ""}
                      onChange={(content) => {
                        setFormData((prev) =>
                          prev.about !== content
                            ? { ...prev, about: content }
                            : prev
                        );
                      }}
                      placeholder="Enter company description..."
                      minHeight="200px"
                    />
                  </GFieldsStacked_0019>
                </PrtnrTextarea>
              </Frame1501>
              <Frame1502>
                <VendorMessageToMembers>
                  Vendor Message to Members
                </VendorMessageToMembers>
                <PrtnrTextarea_0001>
                  <GFieldsStacked_0020>
                    <RichTextEditor
                      label="Message"
                      value={formData?.message || ""}
                      onChange={(content) => {
                        setFormData((prev) =>
                          prev.message !== content
                            ? { ...prev, message: content }
                            : prev
                        );
                      }}
                      placeholder="Enter vendor message to members..."
                      minHeight="100px"
                    />
                  </GFieldsStacked_0020>
                </PrtnrTextarea_0001>
                <Frame1409>
                  <div style={{ width: "100%" }}>
                    <PRTNR.Input
                      label="Stakeholder name"
                      value={formData?.ceo_name}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ceo_name: e.target.value,
                        });
                      }}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <PRTNR.FileInput
                      ref={fieldRefs.picture}
                      label="Stakeholder picture "
                      helpText="JPG, GIF or PNG. Max file size 800kb"
                      accept=".jpg,.jpeg,.gif,.png"
                      onChange={(file) => handleFileChange("picture", file)}
                      name="profile_picture"
                      value={formData?.picture}
                      error={getFieldError("picture")}
                      errorMessage={getFieldErrorMessage("picture")}
                    />
                  </div>
                </Frame1409>
              </Frame1502>
              <Frame1502>
                <VendorMessageToMembers>Trusted by</VendorMessageToMembers>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "14px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 600,
                        color: "rgb(33, 37, 67)",
                      }}
                    >
                      Upload Images (Max 5)
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.gif,.png"
                      multiple
                      onChange={(e) => handleMultipleFileChange(e.target.files)}
                      disabled={
                        formData.trusted_by?.length >= 5 || isUploadingTrustedBy
                      }
                      style={{
                        padding: "8px",
                        border: "1px solid rgb(225, 228, 233)",
                        borderRadius: "8px",
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "14px",
                        opacity: isUploadingTrustedBy ? 0.6 : 1,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        color: "rgb(100, 116, 139)",
                        fontFamily: "Montserrat, sans-serif",
                      }}
                    >
                      JPG, GIF or PNG. Max file size 800kb.{" "}
                      {formData.trusted_by?.length || 0}/5 uploaded
                    </span>
                    {fieldErrors.trusted_by && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "rgb(220, 38, 38)",
                          fontFamily: "Montserrat, sans-serif",
                        }}
                      >
                        {fieldErrors.trusted_by}
                      </span>
                    )}
                  </div>

                  {isUploadingTrustedBy && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "16px",
                        background: "rgb(241, 245, 249)",
                        borderRadius: "8px",
                        border: "1px solid rgb(225, 228, 233)",
                      }}
                    >
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          border: "3px solid rgb(225, 228, 233)",
                          borderTop: "3px solid rgb(94, 93, 240)",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "14px",
                          color: "rgb(33, 37, 67)",
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        Uploading images...
                      </span>
                    </div>
                  )}

                  {formData.trusted_by?.length > 0 && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: "12px",
                        marginTop: "8px",
                      }}
                    >
                      {formData.trusted_by.map((imageUrl, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            width: "100%",
                            paddingBottom: "100%",
                            border: "1px solid rgb(225, 228, 233)",
                            borderRadius: "8px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Trusted by ${index + 1}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeTrustedByImage(index)}
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "4px",
                              background: "rgb(220, 38, 38)",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "16px",
                              fontWeight: "bold",
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Frame1502>
              <Frame1502>
                <VendorMessageToMembers>
                  Paartner Introduction Video
                </VendorMessageToMembers>
                <Frame1409>
                  <PRTNR.Input
                    ref={fieldRefs.video_url}
                    label="Video URL"
                    value={formData?.video_url || ""}
                    onChange={(e) =>
                      handleFieldChange("video_url", e.target.value)
                    }
                    error={getFieldError("video_url")}
                    errorMessage={getFieldErrorMessage("video_url")}
                  />
                </Frame1409>
              </Frame1502>

              {generalError && <Error>{generalError}</Error>}
              <Frame1500>
                <PRTNR.Button
                  variant="white"
                  onClick={() => {
                    openModal();
                  }}
                  disabled={isSubmitting}
                >
                  Discard
                </PRTNR.Button>

                <Frame1399>
                  <PRTNR.Button onClick={handleSave} disabled={isSubmitting}>
                    {isSubmitting
                      ? "Saving..."
                      : isEditing
                      ? "Update"
                      : "Submit"}
                  </PRTNR.Button>
                </Frame1399>
              </Frame1500>
            </Frame1509>
          </Right>

          <Left_0001>
            <Frame1423>
              <Frame1422_0001>
                <Frame1434>
                  <Rewards>Rewards</Rewards>
                </Frame1434>
                <Frame1540>
                  <PRTNR.Input
                    ref={fieldRefs.reward}
                    label="Reward amount"
                    value={formData?.reward}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Allow only digits and ensure it's a positive number
                      if (/^\d*$/.test(newValue)) {
                        handleFieldChange("reward", newValue);
                      }
                    }}
                    required
                    error={getFieldError("reward")}
                    errorMessage={getFieldErrorMessage("reward")}
                    placeholder="Enter amount in £"
                  />
                </Frame1540>
              </Frame1422_0001>
            </Frame1423>

            <Frame1423>
              <Frame1422_0001>
                <Frame1434>
                  <Rewards>Company Details</Rewards>
                </Frame1434>
                <Frame1540>
                  <PRTNR.Input
                    ref={fieldRefs.no_of_employees}
                    label="Number of employees"
                    value={formData.no_of_employees}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      handleFieldChange("no_of_employees", newValue);
                    }}
                    required
                    error={getFieldError("no_of_employees")}
                    errorMessage={getFieldErrorMessage("no_of_employees")}
                  />
                  <PRTNR.Input
                    ref={fieldRefs.year_founded}
                    label="Year founded"
                    value={formData.year_founded}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      // Allow only 4-digit years
                      if (/^\d{0,4}$/.test(newValue)) {
                        handleFieldChange("year_founded", newValue);
                      }
                    }}
                    required
                    error={getFieldError("year_founded")}
                    errorMessage={getFieldErrorMessage("year_founded")}
                    placeholder="YYYY"
                    maxLength={4}
                  />
                  <PRTNR.Input
                    ref={fieldRefs.vendor_hq}
                    label="Vendor Headquarters"
                    value={formData.vendor_hq}
                    onChange={(e) =>
                      handleFieldChange("vendor_hq", e.target.value)
                    }
                    required
                    error={getFieldError("vendor_hq")}
                    errorMessage={getFieldErrorMessage("vendor_hq")}
                  />
                </Frame1540>
              </Frame1422_0001>
            </Frame1423>
          </Left_0001>
        </Wrapper>
      </Dashboard>
      <Footer>
        <Section>
          <Section_0001>
            <Wrapper_0001>
              <Frame38>
                <_2025PaartnerLimitedAllRightsReserved>
                  © 2025 Paartner Limited. All Rights Reserved
                </_2025PaartnerLimitedAllRightsReserved>
              </Frame38>
            </Wrapper_0001>
          </Section_0001>
        </Section>
      </Footer>
      <GenericModal isOpen={isModalOpen} onClose={closeModal}>
        <ModalAdminDiscard
          onClose={closeModal}
          setOldData={setFormData}
          data={vendorFromNav || {}}
          navigate_handler={() =>
            navigate("/adminvendorsone", { state: { vendor: formData } })
          }
        />
      </GenericModal>
      <GenericModal isOpen={isSuccessModalOpen} onClose={closeSuccessModal}>
        <ModalUpdateSuccess
          onClose={() => {
            closeSuccessModal();
            navigate("/adminvendorsone", { state: { vendor: formData } });
          }}
        />
      </GenericModal>
      <GenericModal
        isOpen={isErrorModalOpen}
        onClose={closeErrorModal}
        bg="rgb(226, 232, 240)"
      >
        <ModalAdminAddError
          isOpen={isErrorModalOpen}
          onClose={closeErrorModal}
        />
      </GenericModal>
    </RootWrapperAdminvendorsthree>
  );
}

const RootWrapperAdminvendorsthree = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  max-height: 100vh;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
`;

const Dashboard = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  background: rgb(241, 245, 249);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 0px 60px;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  max-width: 1200px;
  width: 100%;
  box-sizing: border-box;
  padding: 32px 0px;
`;

const Left_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  gap: 8px;
  width: 298px;
  box-sizing: border-box;
`;

const Frame1423 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1422_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 20, 0.03);
  border: solid 1px rgb(225, 228, 234);
  border-radius: 16px;
  width: 326px;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
`;

const Frame1434 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Rewards = styled.span`
  display: flex;
  height: 29px;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 0;
  color: var(--Color-Text-Text, #212543);

  /* P2 */
  font-family: Montserrat;
  font-size: var(--Fonts-P2, 18px);
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 28.8px */
  flex: 1;
`;

const Frame1540 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 0px 24px;
  flex-shrink: 0;
`;

const Right = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 24px;
  height: auto;
  box-sizing: border-box;
  max-width: 878px;
  width: 100%;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1462 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
`;

const LinkButton = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const WelcomeJames = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: center;
`;

const Widthdraw = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  box-sizing: border-box;
`;
const Frame1389 = styled.div`
  max-height: 297px;
  min-height: 297px;
  overflow: hidden;
  border: solid 1px rgba(136, 136, 136, 0.1);
  background: #e0e4f4;
  border-radius: 16px;
  position: relative;
  align-self: stretch;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Image20 = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;
const Logo_0001 = styled.div`
  display: flex;
  width: 260px;
  height: 150px;
  padding: var(--Padding-Normal, 16px) 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Padding-Normal, 16px);
  flex-shrink: 0;
  border-radius: var(--Radius-Card-Normal, 16px);
  background: #e0e4f4;

  /* Card Shadow */
  box-shadow: 0 7px 30px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  align-self: flex-start;
  @media (max-width: 1024px) and (min-width: 768px) {
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;
const ImageLogo = styled.img`
  max-width: 100%;
  width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
`;
const Frame1509 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  gap: 29px;
  align-self: stretch;
  height: auto;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GeneralInformation = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
`;

const Frame1409 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  margin-top: 18px;
`;

const Frame1406 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
  border: solid 1px rgb(225, 228, 233);
  background: rgb(255, 255, 255);
  border-radius: 16px;
`;

const VendorInformation = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;
const LabelWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 4px;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;
const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;
const GFeildsInfo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: cover;
`;

const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 125%; /* Show below the icon */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: 280px;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, #ffffff);
  font-family: "Montserrat", sans-serif;
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, #ffffff)
      transparent;
  }
`;
const Frame1407 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1518 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const IdealClientProfile = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1501 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border: solid 1px rgb(225, 228, 233);
  border-radius: 16px;
  align-self: stretch;
  /* height: 334px; */
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const About = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
`;

const PrtnrTextarea = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  /* height: 225px; */
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFieldsStacked_0019 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  align-self: stretch;
  /* height: 225px; */
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1502 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border: solid 1px rgb(225, 228, 233);
  border-radius: 16px;
  align-self: stretch;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 16px;
  flex-shrink: 0;
`;

const VendorMessageToMembers = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 24px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  text-align: left;
`;

const PrtnrTextarea_0001 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFieldsStacked_0020 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 4px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1500 = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: flex-end;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;
const Error = styled.div`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
`;

const Frame1399 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 16px;
  box-sizing: border-box;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Section = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  background: rgb(33, 37, 67);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 24px;
  flex-shrink: 0;
`;

const Section_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  padding: 0px 60px;
  flex-shrink: 0;
`;

const Wrapper_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame38 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const _2025PaartnerLimitedAllRightsReserved = styled.span`
  color: white;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const BackContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
const BackText = styled.span`
  color: #64748b;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: left;
`;

const SendButton = styled.button`
  background-color: #5e5df0;
  color: white;
  border: none;
  padding: 0 12px;
  height: 36px;
  font-weight: 600;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background-color: #4b4acb;
  }
`;

// Add CSS keyframes for spinner animation
const style = document.createElement("style");
style.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);
